import React, { useState } from 'react'
import { Formik } from 'formik'
import { useQuery, useMutation } from '../../Hooks/graphql_utils'
import { gql } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../Hooks/toast'
import { DailyYardageSchema } from '@mmg/validation'
import { DailyYardageForm } from './DailyYardageForm.js'
import { ErrorMessage, Spinner } from '../../Elements'
import {filterCrewNamesBySubdivision } from '../../utils/helpers'

const FRAGMENT = gql`
    fragment dailyYardageFragment on Job {
        id
        company {
            id
            company_name
        }
        name
        address_line_1
        address_line_2
        city
        sub_jobs {
            id
            mmg_perimeter
            is_cash_on_delivery
            is_in_print_queue
            awaiting_builder_date
            job_types {
                id
                name
            }
            job_category {
                id
                name
            }
            status {
                id
                name
            }
            gunite_details {
                id
                job_detail_id
                finished_date
                total_yardage
                flash_yards
                is_spa_negative_edge
                is_spa_rolled_edge
                is_flash
                gunite_history {
                    id
                    job_detail_id
                    gunite_date
                    status {
                        id
                        name
                    }
                    crew {
                        id
                        name
                    }
                    type {
                        id
                        name
                    }
                    yardage
                    job_order {
                        id
                        name
                    }
                    notes
                }
            }
        }
    }
`

const QUERY = gql`
    ${FRAGMENT}
    query dailyYardageQuery ($id: ID!) {
        job(id: $id) {
            ...dailyYardageFragment
        }
        lookupJobStatus {
            id
            name
            show_gunite
            show_gunite_history
        }
        lookupCrewTypes {
            id
            name
            subdivision
        }
        lookupJobOrder{
            id
            name
        }
        lookupGuniteHistoryTypes{
            id
            name
        }
    }
`

const MUTATION = gql`
    mutation UpdateDailyYardage($id: ID!, $dailyYardage: DailyYardageInput!) {
        updateDailyYardage(id: $id, dailyYardage: $dailyYardage) {
            ...dailyYardageFragment
        }
    }
    ${FRAGMENT}
`

export default () => {
    const { jobID } = useParams()
    const navigate = useNavigate()
    const { showSuccess, showError } = useToast()
    const [updateDailyYardage] = useMutation(MUTATION)
    const [trackedInitialState, setTrackedInitialState] = useState(null)

    const { loading, error, data } = useQuery(QUERY, {
        variables: {
            id: jobID
        },
        onCompleted: (data) => setTrackedInitialState(data.job)
    })

    if (loading || !trackedInitialState) {
        return (
            <Spinner />
        )
    }

    if (error) {
        return (
            <ErrorMessage/>
        )
    }

    const onSubmit = async (values, { setValues }) => {
        values.sub_jobs = values.sub_jobs.filter(sj => sj.job_category && sj.job_category.id && sj.job_category.id === 1)
        const model = DailyYardageSchema.cast(values, { stripUnknown: true })
        await updateDailyYardage({
            variables: {
                id: jobID,
                dailyYardage: model
            }
        })
            .then(({ data }) => {
                setTrackedInitialState(data.updateDailyYardage)
                setValues(data.updateDailyYardage)
                showSuccess('Success!')
            })
            .catch(() => showError('Error, please try again.'))
    }

    const onReset = () => navigate('/job')
    
    const maskedJobStatuses = data.lookupJobStatus.filter(s => s.show_gunite).map(s => ({
        ...s,
        name: s.name === 'Weeping' ? 'Cave in' : s.name
    }))
    const guniteHistoryEntryJobStatuses = data.lookupJobStatus.filter(s => s.show_gunite_history).map(s => ({
        ...s,
        name: s.name === 'Weeping' ? 'Cave in' : s.name
    }))

    return (
        <Formik
            initialValues={trackedInitialState}
            onSubmit={onSubmit}
            enableReinitialize
            render={props =>
                <DailyYardageForm
                    {...props}
                    crews={filterCrewNamesBySubdivision(data.lookupCrewTypes, 'gunite')}
                    jobStatuses={maskedJobStatuses}
                    guniteHistoryEntryJobStatuses={guniteHistoryEntryJobStatuses}
                    onCancel={onReset}
                    jobOrders={data.lookupJobOrder}
                    guniteHistoryTypes={data.lookupGuniteHistoryTypes}
                />
            }
            validationSchema={DailyYardageSchema}
        />
    );
}